import {injectAutoEffect} from 'ngxtension/auto-effect';

import {getState, signalStoreFeature, SignalStoreFeatureResult, withHooks} from '@ngrx/signals';

export type WithStateValidationFn<S extends SignalStoreFeatureResult['state'] = SignalStoreFeatureResult['state']> = (state: S, initialState: S) => string | boolean;

function filterPrivateStateKeys<K extends string>(key: K): boolean {
	return !key.startsWith('_');
}

function defaultStateValidationFn<S extends SignalStoreFeatureResult['state']>(state: S, initialState: S): string | boolean {
	// the default fn check the state/keys existence (the state should be a subset of the initialState)
	const acceptedKeys = Object.keys(initialState).filter(filterPrivateStateKeys);
	const stateKeys = Object.keys(state).filter(filterPrivateStateKeys);
	const keysReport = {
		acceptedKeys,
		stateKeys,
		NON_ACCEPTED_KEYS: stateKeys.filter(key => !acceptedKeys.includes(key)),
	};
	return keysReport.NON_ACCEPTED_KEYS.length ? `The state is missing keys:\n${JSON.stringify(keysReport, null, 2)}` : true;
}

/**
 * Adds state validation functionality as a feature to a ngrx `SignalStore`.
 * @template Input - The type of the SignalStore feature's input.
 * @param initialState - The initial state of the store.
 * @param validationFn - The function to use to validate the state.
 * @returns The SignalStore feature with state validation functionality applied.
 */
export function withStateValidator() {
	return signalStoreFeature(
		withHooks(store => ({
			onInit: function () {
				const storeChangesEffect = injectAutoEffect();
				const storeInitialState = getState(store);
				storeChangesEffect(() => {
					const state = getState(store);
					if (state) {
						const validationResult = defaultStateValidationFn(state, storeInitialState);
						if (typeof validationResult === 'string') {
							throw new Error(`State validation failed: ${validationResult}`);
						}
					}
				});
			},
		})),
	);
}
